import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { useT } from "@/context/LanguageContext";
import { PlayCircle, X } from "@phosphor-icons/react";

export function VideoShowcase({ videos = [] }) {
  const { t } = useT();
  const [active, setActive] = useState(null);
  return (
    <section className="bg-ink-950 py-24 lg:py-32" data-testid="video-showcase-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading
            overline={t("video.overline")}
            title={t("video.title")}
            subtitle={t("video.subtitle")}
            light
          />
          <Link to="/videos" className="shrink-0 text-sm font-semibold text-teal-light hover:underline" data-testid="videos-view-all">
            {t("video.viewAll")}
          </Link>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {videos.map((v, i) => (
            <Reveal key={v.title} delay={(i % 4) * 0.07}>
              <button
                onClick={() => setActive(v)}
                data-testid={`video-card-${i}`}
                className="group block w-full overflow-hidden rounded-xl border border-white/10 bg-white/5 text-left"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={v.thumbnail} alt={v.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <PlayCircle size={48} weight="fill" className="text-white/90 transition-transform group-hover:scale-110" />
                  </div>
                  <span className="absolute bottom-2 right-2 rounded bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">{v.duration}</span>
                </div>
                <div className="p-4">
                  <p className="text-[11px] uppercase tracking-wider text-teal-light">{v.category}</p>
                  <p className="mt-1 font-heading text-sm font-semibold leading-snug text-white">{v.title}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-5" onClick={() => setActive(null)}>
          <div className="relative aspect-video w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActive(null)} className="absolute -top-10 right-0 text-white" data-testid="video-modal-close">
              <X size={28} />
            </button>
            <iframe
              className="h-full w-full rounded-lg"
              src={`https://www.youtube.com/embed/${active.youtube_id}`}
              title={active.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}
